import { useEffect, useState, useRef } from "react";
import { AiFillCalendar } from "react-icons/ai";
import colors from "../../config/colors";
import config from "../../config/config";
import AppInput from "../../common/AppInput";
import { IoLocationSharp } from "react-icons/io5";
import { useQuery } from "@tanstack/react-query";
import { getOrganizations } from "../../stores/OrganizationStore";
import { getLocations } from "../../stores/LocationStore";
import useJob from "../../hooks/useJob";
import { getJobsWithNoPos } from "../../stores/JobStore";
import { getPosGroupedByJob } from "../../stores/PoStore";
import { getPaymentsGroupedByJob } from "../../stores/PaymentStore";
import html2pdf from "html2pdf.js";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { FaSpinner } from "react-icons/fa";
import RpoTemplate from "./RpoTemplate";

const formInit = {
  fromDate: "",
  toDate: "",
  clients: [],
  locations: [],
};

const RpoTab = () => {
  const [form, setForm] = useState({ ...formInit });
  const [locationsState, setLocationsState] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [currenRpoData, setCurrenRpoData] = useState(null);
  const [errors, setErrors] = useState([]);
  const rpoRef = useRef(null);

  const { data: organizations = [] } = useQuery({
    queryFn: () => getOrganizations(),
    queryKey: ["organizations"],
  });

  const { data: locationsData = [], refetch } = useQuery({
    queryFn: () => getLocations({ organizations: form.clients }),
    queryKey: ["locations"],
    enabled: false,
  });

  useEffect(() => {
    refetch();

    if (!form.clients.length) {
      setForm((cur) => ({ ...cur, locations: [] }));
      setLocationsState([]);
    }
  }, [form.clients]);

  useEffect(() => {
    if (locationsData) {
      setLocationsState(locationsData);
    }
  }, [locationsData]);

  const {
    data: jobs,
    refetch: refetchJobs,
    isFetching,
  } = useQuery({
    queryFn: () =>
      getJobsWithNoPos({
        locations: form.locations.length
          ? (form.locations[0]?._id === "all"
              ? locationsState
              : form.locations
            )?.map((loc) => loc._id)
          : [],
        startDate: form.fromDate,
        endDate: form.toDate,
        middleDateType: "jobStartDateAsDate",
      }),
    queryKey: [
      "jobs",
      form.locations.length || locationsState.length,
      form.startDate,
      form.endDate,
      "noPo",
    ],
    enabled: false,
  });

  const { data: posGroupedByJob } = useQuery({
    queryFn: () => getPosGroupedByJob(),
    queryKey: ["pos", "groupedByJob"],
  });

  const { data: paymentsGroupedByJob } = useQuery({
    queryFn: () => getPaymentsGroupedByJob(),
    queryKey: ["jobPayments", "groupedByJob"],
  });

  const handleChange = ({ target }) => {
    const name = target.name;
    const value = target.value;

    if (name === "toDate") {
      if (new Date(form.jobEndDate).valueOf() < new Date(value).valueOf()) {
        delete form.jobEndDate;
      }
    }
    setForm({ ...form, [name]: value });
  };

  const handleSelect = ({ target }) => {
    const name = target.name;
    let value = target.value;

    if (value?.length) {
      const allExist = value?.find((loc) => loc._id === "all");
      if (allExist) {
        value = [{ name: "All", _id: "all" }];
      }
      setForm({
        ...form,
        [name]: value,
      });
    } else {
      setForm({
        ...form,
        [name]: [],
      });
    }
  };

  useEffect(() => {
    if (form.fromDate && form.toDate) {
      refetchJobs();
    }
  }, [form]);

  const data = jobs?.map((jb) => {
    const pos = posGroupedByJob?.[jb._id];
    const po = pos ? pos[pos.length - 1] : undefined;
    const locations = jb?.location?.map((loc) => {
      const found = locationsState?.find((item) => item._id === loc);
      return found;
    });
    return {
      ...jb,
      organization: organizations?.find((i) => i._id === jb.organization),
      organizationName: organizations?.find((i) => i._id === jb.organization)
        ?.name,
      location: locations,
      poNo: po?.poNo || " - ",
      payments: paymentsGroupedByJob?.[jb._id],
    };
  });

  const generatePDFBlob = async (element, filename) => {
    const opt = {
      margin: 0,
      filename: filename,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        letterRendering: true,
        logging: false,
      },
      jsPDF: {
        unit: "in",
        format: "letter",
        orientation: "portrait",
      },
    };

    try {
      // Generate PDF and return as blob
      const pdfBlob = await html2pdf().from(element).set(opt).outputPdf("blob");

      return pdfBlob;
    } catch (error) {
      console.error("Error generating PDF:", error);
      throw error;
    }
  };

  const onDownloadHandler = async () => {
    if (!data || data.length === 0) return;

    setIsGenerating(true);
    setProgress({ current: 0, total: data.length });
    setErrors([]);

    const zip = new JSZip();
    const failedRpos = [];

    for (let i = 0; i < data.length; i++) {
      try {
        // Update current Rpo to render
        setCurrenRpoData(data[i]);
        setProgress({ current: i + 1, total: data.length });

        // Wait for DOM to update and render
        await new Promise((resolve) => setTimeout(resolve, 300));

        if (!rpoRef.current) {
          throw new Error("Rpo template ref not found");
        }

        // Generate PDF blob
        const filename = `RPO - ${data[i].organizationName} - ${data[i].jobId}.pdf`;
        const pdfBlob = await generatePDFBlob(rpoRef.current, filename);

        // Add to zip
        zip.file(filename, pdfBlob);
      } catch (error) {
        console.error(`Failed to generate Rpo ${i + 1}:`, error);
        failedRpos.push({
          index: i + 1,
          jobId: data[i]?.jobId || "Unknown",
          organizationName: data[i]?.organizationName || "Unknown",
          error: error.message,
        });
      }
    }

    // Generate and download zip
    try {
      if (Object.keys(zip.files).length === 0) {
        alert("No Rpos were successfully generated.");
        setIsGenerating(false);
        setCurrenRpoData(null);
        setProgress({ current: 0, total: 0 });
        return;
      }

      const zipBlob = await zip.generateAsync({
        type: "blob",
        compression: "DEFLATE",
        compressionOptions: { level: 6 },
      });

      const zipFilename = `Rpos-${
        new Date(form.fromDate).toISOString().split("T")?.[0] || "all"
      }-to-${new Date(form.toDate).toISOString().split("T")?.[0] || "all"}.zip`;
      saveAs(zipBlob, zipFilename);

      // Show success/error message
      if (failedRpos.length > 0) {
        setErrors(failedRpos);
        const successCount = data.length - failedRpos.length;
        alert(
          `Successfully generated ${successCount} of ${data.length} Rpos.\n` +
            `${failedRpos.length} Rpo(s) failed. See details below.`
        );
      } else {
        alert(`Successfully generated all ${data.length} Rpos!`);
      }
    } catch (error) {
      console.error("Failed to create zip file:", error);
      alert("Failed to create zip file. Please try again.");
    }

    setIsGenerating(false);
    setCurrenRpoData(null);
    setProgress({ current: 0, total: 0 });
  };

  const size = config.iconSize;

  return (
    <div>
      {/* Loading Overlay */}
      {isGenerating && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.85)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
            flexDirection: "column",
            gap: 20,
          }}
        >
          <div
            style={{
              width: 60,
              height: 60,
              border: "5px solid #f3f3f3",
              borderTop: "5px solid #3B7FF6",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }}
          />
          <p style={{ color: "white", fontSize: "1.5rem", fontWeight: "bold" }}>
            Generating Rpo {progress.current} of {progress.total}...
          </p>
          <div
            style={{
              width: "400px",
              height: "20px",
              backgroundColor: "#333",
              borderRadius: "10px",
              overflow: "hidden",
              border: "2px solid #555",
            }}
          >
            <div
              style={{
                width: `${(progress.current / progress.total) * 100}%`,
                height: "100%",
                backgroundColor: "#3B7FF6",
                transition: "width 0.3s ease",
              }}
            />
          </div>
          <p style={{ color: "#ccc", fontSize: "1rem" }}>
            Please do not close or navigate away from this page
          </p>
        </div>
      )}

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>

      <h5 style={{ textAlign: "center" }}>
        Note: All RPOs corresponding to jobs that have not yet received a
        Purchase Order (PO) can be downloaded for the specified date range.
      </h5>
      <div style={{ display: "flex", gap: 300 }}>
        <AppInput
          onChange={handleChange}
          name="fromDate"
          label="From"
          placeholder="From"
          datePicker
          floatLabel
          value={form.fromDate ? new Date(form.fromDate) : ""}
          required
          type="date"
          leftIcon={
            <AiFillCalendar color={colors.silver} size={config.iconSize} />
          }
          style={{ maxWidth: 400, minWidth: 400 }}
        />
        <AppInput
          onChange={handleChange}
          name="toDate"
          label="To"
          placeholder="To"
          datePicker
          minDate={form.fromDate ? new Date(form.fromDate) : ""}
          value={form.toDate ? new Date(form.toDate) : ""}
          required
          floatLabel
          type="date"
          leftIcon={
            <AiFillCalendar color={colors.silver} size={config.iconSize} />
          }
          style={{ maxWidth: 400, minWidth: 400 }}
        />
      </div>
      <div style={{ display: "flex", gap: 300 }}>
        <AppInput
          type="multi"
          label="Client"
          name="clients"
          chip
          onChange={handleSelect}
          leftIcon={<IoLocationSharp size={size} color={"silver"} />}
          options={
            form.clients[0]?._id === "all"
              ? [{ name: "All", _id: "all" }]
              : [
                  { name: "All", _id: "all" },
                  ...organizations
                    ?.filter((i) => i !== undefined)
                    ?.map((i) => {
                      return {
                        ...i,
                      };
                    }),
                ]
          }
          filterBy="name"
          value={form.clients}
          required
          floatLabel
          style={{ maxWidth: 400, minWidth: 400 }}
        />
        <AppInput
          type="multi"
          label="Job Location"
          name="locations"
          chip
          onChange={handleSelect}
          leftIcon={<IoLocationSharp size={size} color={"silver"} />}
          options={
            form.locations[0]?._id === "all"
              ? [{ name: "All", _id: "all" }]
              : [
                  { name: "All", _id: "all" },
                  ...locationsState
                    ?.filter((i) => i !== undefined)
                    ?.map((i) => {
                      return {
                        ...i,
                        name: `${i.tag} - ${i.title}`,
                      };
                    }),
                ]
          }
          filterBy="name"
          value={form.locations}
          required
          floatLabel
          disabled={!locationsState?.length}
          style={{ maxWidth: 400, minWidth: 400 }}
        />
      </div>
      <div style={{ display: "flex", gap: 300 }}>
        <div style={{ minWidth: 300 }}></div>
        <div
          style={{
            border: "1px solid #000",
            borderRadius: 7,
            padding: 20,
            marginTop: 25,
            textAlign: "center",
          }}
        >
          <p style={{ fontSize: "2.5rem", fontWeight: "bold" }}>
            {isFetching ? <FaSpinner /> : data?.length || 0}
          </p>
          <p style={{ fontSize: "1rem" }}>RPOs Available to Download</p>
        </div>
      </div>

      {errors.length > 0 && (
        <div
          style={{
            marginTop: 20,
            padding: 15,
            backgroundColor: "#fff3cd",
            border: "1px solid #ffc107",
            borderRadius: 5,
            maxHeight: "200px",
            overflowY: "auto",
          }}
        >
          <p style={{ fontWeight: "bold", marginBottom: 10, color: "#856404" }}>
            ⚠️ Failed to generate {errors.length} Rpo(s):
          </p>
          {errors.map((err, idx) => (
            <div
              key={idx}
              style={{ fontSize: "0.9rem", margin: "5px 0", color: "#856404" }}
            >
              <strong>• Rpo #{err.index}:</strong> {err.organizationName} -{" "}
              {err.jobId}
              <br />
              <span style={{ fontSize: "0.85rem", paddingLeft: "15px" }}>
                Error: {err.error}
              </span>
            </div>
          ))}
        </div>
      )}

      <div
        style={{
          marginTop: 60,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 20,
        }}
      >
        <button
          style={{
            padding: "10px 25px",
            borderRadius: 5,
            backgroundColor: !data || data.length === 0 ? "#ccc" : "#3B7FF6",
            border: "none",
            color: "#fefefe",
            cursor: !data || data.length === 0 ? "not-allowed" : "pointer",
            opacity: !data || data.length === 0 ? 0.6 : 1,
          }}
          onClick={onDownloadHandler}
          disabled={!data || data.length === 0 || isGenerating}
        >
          {isGenerating ? "Generating..." : "Download"}
        </button>
        <button
          style={{
            padding: "10px 25px",
            borderRadius: 5,
            backgroundColor: "#DC143C",
            border: "none",
            color: "#fefefe",
            cursor: isGenerating ? "not-allowed" : "pointer",
            opacity: isGenerating ? 0.6 : 1,
          }}
          onClick={() => {
            setForm({ ...formInit });
            setErrors([]);
          }}
          disabled={isGenerating}
        >
          Reset
        </button>
      </div>

      {/* Hidden Rpo template for PDF generation */}
      <div
        style={{
          position: "absolute",
          left: "-9999px",
          top: 0,
          width: "8.5in",
        }}
      >
        <div ref={rpoRef}>
          <RpoTemplate
            data={currenRpoData || data?.[0] || {}}
            targetRef={rpoRef}
          />
        </div>
      </div>
    </div>
  );
};

export default RpoTab;
